import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../utils/api";
import { cardCls } from "../ui/styleTokens";
import { MdFileDownload, MdOutlineVisibility, MdClose } from "react-icons/md";
import Modal from "../Modal";

const SkeletonRow = () => (
    <div className="flex items-center gap-4 py-4 animate-pulse">
        <div className="h-3.5 w-24 bg-gray-200 rounded" />
        <div className="h-3.5 w-32 bg-gray-100 rounded flex-1" />
        <div className="h-3.5 w-20 bg-gray-100 rounded" />
        <div className="h-8 w-8 bg-gray-100 rounded-lg" />
        <div className="h-8 w-8 bg-gray-100 rounded-lg" />
    </div>
);

const InvoiceHistoryTab = () => {
    const blobCache = useRef({});
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [previewingId, setPreviewingId] = useState(null);
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        axiosInstance.get("/billing/invoices")
            .then((r) => setInvoices(r.data || []))
            .catch((err) => console.error("Failed to load invoices", err))
            .finally(() => setLoading(false));
    }, []);

    const fetchPdfBlob = async (invoice) => {
        if (blobCache.current[invoice._id]) return blobCache.current[invoice._id];
        const res = await axiosInstance.get(`/billing/invoices/${invoice._id}/pdf`, {
            responseType: "blob",
        });
        const blob = new Blob([res.data], { type: "application/pdf" });
        blobCache.current[invoice._id] = blob;
        return blob;
    };
    
    const handleDownload = async (invoice) => {
        if (downloadingId || previewingId) return;
        setDownloadingId(invoice._id);
        try {
            const blob = await fetchPdfBlob(invoice);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${invoice.invoiceNumber}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Failed to download invoice", err);
        } finally {
            setDownloadingId(null);
        }
    };

    const handlePreview = async (invoice) => {
        if (downloadingId || previewingId) return;
        setPreviewingId(invoice._id);
        try {
            const blob = await fetchPdfBlob(invoice);
            setPreviewUrl(window.URL.createObjectURL(blob));
            setPreviewInvoice(invoice);
        } catch (err) {
            console.error("Failed to preview invoice", err);
        } finally {
            setPreviewingId(null);
        }
    };

    const closePreview = () => {
        if (previewUrl) window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewInvoice(null);
    };

    return (
        <div className={`${cardCls} p-5`}>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">Invoice history</h4>
            <p className="text-sm text-gray-500 mb-4">Every invoice created, across all events.</p>

            <div className="divide-y divide-gray-100">
                {loading ? (
                    <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
                ) : invoices.length === 0 ? (
                    <p className="text-sm text-gray-400 py-8 text-center">
                        No invoices yet. Create one from the Create Invoice tab.
                    </p>
                ) : (
                    invoices.map((inv) => (
                        <div key={inv._id} className="flex items-center gap-4 py-4">
                            <div className="w-36 shrink-0">
                                <p className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</p>
                                <p className="text-xs text-gray-400">{new Date(inv.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-800 truncate">{inv.clientName}</p>
                                <p className="text-xs text-gray-400 truncate">{inv.event?.name}</p>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 tabular-nums w-24 text-right">
                                {inv.total.toFixed(2)} MAD
                            </p>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={() => handlePreview(inv)}
                                    disabled={previewingId === inv._id}
                                    title="Preview"
                                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-600 transition disabled:opacity-50"
                                >
                                    <MdOutlineVisibility size={18} />
                                </button>
                                <button
                                    onClick={() => handleDownload(inv)}
                                    disabled={downloadingId === inv._id}
                                    title="Download PDF"
                                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-600 transition disabled:opacity-50"
                                >
                                    <MdFileDownload size={18} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <Modal isOpen={!!previewInvoice} onClose={closePreview} title={previewInvoice?.invoiceNumber || "Invoice"} size="lg">
                <div className="flex flex-col h-full">
                    <div className="flex-1 bg-gray-100" style={{ minHeight: "75vh" }}>
                        {previewUrl && (
                            <iframe
                                src={previewUrl}
                                title="Invoice preview"
                                className="w-full h-full"
                                style={{ minHeight: "75vh", border: "none" }}
                            />
                        )}
                    </div>
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                        <button
                            onClick={closePreview}
                            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold text-sm"
                        >
                            Close
                        </button>
                        <button
                            onClick={() => previewInvoice && handleDownload(previewInvoice)}
                            disabled={!!downloadingId || !!previewingId}
                            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm shadow-sm disabled:opacity-60"
                        >
                            {downloadingId === previewInvoice?._id ? "Downloading…" : "Download"}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default InvoiceHistoryTab;